import { Identifier } from 'src/types/utilityTypes';
import api from 'src/utils/ajax';
import { camelcaseKeys } from 'src/utils/camelize-keys';

export type QuickLink = {
  id: number;
  name: string;
  filters: number | string;
  createdAt: number;
  linkerId?: Identifier;
  params?: Record<string, any>;
};

export type quickLinkTypes = 'clients' | 'plans' | 'report' | 'inventory' | 'schedule';

const metaForType = {
  clients: {
    linker_id: 1,
    linker_type: 'Platform',
    model_type: 'client',
    model_type_eq: 1,
  },
  plans: {
    linker_id: 1,
    linker_type: 'Platform',
    model_type: 'plan',
    model_type_eq: 0,
  },
  report: {
    linker_id: 1,
    linker_type: 'Report',
    model_type: 'report',
    model_type_eq: 3,
  },
  inventory: {
    linker_id: 1,
    linker_type: 'Platform',
    model_type: 'inventory',
    model_type_eq: 4,
  },
  schedule: {
    linker_id: 1,
    linker_type: 'User',
    model_type: 'schedule',
    model_type_eq: 2,
  },
};

export function normalizeQuickLink(quickLink): QuickLink {
  const camelized = camelcaseKeys(quickLink, { deep: true });
  const parsedParams = new URLSearchParams(camelized.filters);

  const obj = {};
  parsedParams.forEach((value, key) => {
    obj[key] = value;
  });
  const params = camelcaseKeys(obj) as any;

  return {
    ...camelized,
    params: params,
  };
}

export const fetchQuickLinks = async ({
  type = 'clients',
  linkerId,
}: {
  type: quickLinkTypes;
  linkerId?: Identifier;
}) => {
  const response = await api.get('/api/v3/quick_links', {
    params: {
      fetch_all: true,
      q: {
        model_type_eq: metaForType[type].model_type_eq,
        linker_type_eq: metaForType[type].linker_type,
        ...(linkerId && { linker_id_eq: linkerId }),
      },
    },
  });

  return response.data?.map((ql) => normalizeQuickLink(ql));
};

export const fetchQuickLinkById = async (
  id: number
): Promise<QuickLink | null> => {
  const response = await api.get('/api/v3/quick_links', {
    params: { 'q[id_eq]': id },
  });
  const results = (response.data?.quick_link || []).map((ql) =>
    normalizeQuickLink(ql)
  );
  return results[0] ?? null;
};

export const addQuickLink = async ({
  type = 'clients',
  filters,
  name,
  linkerId,
}: {
  type: quickLinkTypes;
  linkerId: Identifier;
  filters: string;
  name: string;
}) => {
  const response = await api.post(`/api/v3/quick_links`, {
    quick_link: {
      filters,
      name,
      linker_id: linkerId,
      linker_type: metaForType[type].linker_type,
      model_type: metaForType[type].model_type,
    },
  });

  return normalizeQuickLink(response.data);
};

export const updateQuickLink = async ({
  id,
  updatedQuickLink,
}: {
  type: quickLinkTypes;
  id: string | number;
  updatedQuickLink: Partial<QuickLink>;
}) => {
  const response = await api.put(`/api/v3/quick_links/${id}`, {
    quick_link: {
      ...updatedQuickLink,
    },
  });

  return normalizeQuickLink(response.data);
};

export const deleteQuickLink = async ({
  id,
}: {
  type: quickLinkTypes;
  id: string | number;
}) => {
  const response = await api.delete(`/api/v3/quick_links/${id}`);
  return normalizeQuickLink(response.data);
};
